import React, { useEffect, useRef, useState } from 'react'
import clsx from 'clsx'
import { ReactComponent as Arrow } from '@/assets/icons/thin-arrow-purple.svg'
import { DropdownMenuProps } from '@/components/organisms/SectionCaseStudiesGrid/types'

export function CategoriesMenu({ setActiveCategory, categories, className, activeCategory }: DropdownMenuProps) {
  const handleClick = (item: string | undefined) => {
    if (setActiveCategory && item) {
      setActiveCategory(item)
    }
  }

  const getMenuItemClassname = (item: string | undefined) =>
    clsx(
      'cursor-pointer border-1 border-white w-full text-start rounded font-bold text-text-dark-900 netbook:px-24 netbook:py-12 p-10 hover:bg-primary-100 transition capitalize',
      {
        '!border-1 !border-primary-500 bg-primary-100 !text-primary-600 font-bold': activeCategory === item
      }
    )

  return (
    <div id="dropdown" className={clsx('flex flex-col gap-4 bg-white border-grey-700 border-1 p-8 rounded', className)}>
      <button
        type="button"
        aria-label="Show all stories"
        onClick={() => handleClick('all')}
        className={clsx(getMenuItemClassname('all'), '!text-bodyM')}
      >
        All
      </button>
      {categories.length > 0 &&
        categories.map((item) => (
          <button
            type="button"
            aria-label={`Show ${item} stories`}
            onClick={() => handleClick(item)}
            className={clsx(getMenuItemClassname(item), '!text-bodyM')}
          >
            {item}
          </button>
        ))}
    </div>
  )
}

export function DropdownMenu({
  setActiveCategory,
  activeCategory,
  categories,
  className,
  industry_label
}: DropdownMenuProps) {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const handleClick = (item: string) => {
    if (setActiveCategory) {
      setActiveCategory(item)
    }
    setIsOpen(false)
  }

  const handleOutsideClick = (event: MouseEvent) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
      setIsOpen(false)
    }
  }

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('mousedown', handleOutsideClick)
    } else {
      document.removeEventListener('mousedown', handleOutsideClick)
    }

    // Cleanup listener on component unmount
    return () => document.removeEventListener('mousedown', handleOutsideClick)
  }, [isOpen])

  return (
    <div
      ref={dropdownRef}
      className={clsx('flex flex-col gap-2 relative z-20 netbook:max-w-[250px] w-full', className)}
    >
      <p className="text-bodyL mb-4 text-text-dark-900">{industry_label}</p>
      <button
        type="button"
        aria-label="Open Industry Select menu"
        className={clsx(
          'flex w-full items-center justify-between cursor-pointer border-1 border-grey-100 text-bodyL text-start rounded font-semibold text-text-dark-900 py-12 px-24 transition capitalize',
          isOpen && 'border-grey-700'
        )}
        onClick={() => setIsOpen(!isOpen)}
      >
        {activeCategory === 'all' ? 'all' : activeCategory}
        <Arrow
          className={clsx(isOpen ? '-rotate-90' : 'rotate-90', 'duration-300 [&>path]:stroke-text-dark-600')}
          aria-hidden="true"
        />
      </button>
      {isOpen && (
        <div className="z-10 h-full w-full absolute -bottom-[calc(100%+8px)] left-0 bg-white" aria-expanded={isOpen}>
          <CategoriesMenu
            activeCategory={activeCategory}
            categories={categories}
            setActiveCategory={handleClick}
            className="bg-white p-8 border-1 border-grey-100 shadow-md rounded"
          />
        </div>
      )}
    </div>
  )
}
